/**
 * Farm "Health" dashboard tab. Subscribes to live `farm-health` window events
 * (forwarded from the farm's ping) and accumulates a rolling 1-hour window of
 * samples *in memory only* — nothing is fetched or persisted. Opening the tab
 * starts from empty and fills as samples arrive (every ~30s); navigating away
 * discards everything (the listener self-removes once its panel leaves the DOM).
 */
import { t } from '../ui/lang';
const WINDOW_MS = 60 * 60 * 1000; // 1 hour
function fmtUptime(sec) {
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0)
        return t('farmdev.uptime_dh', { d, h });
    if (h > 0)
        return t('farmdev.uptime_hm', { h, m });
    return t('farmdev.uptime_m', { m });
}
/** Draw a 0..max line chart of {t, v} points across a fixed time window. */
function drawChart(canvas, points, nowTs, max, color) {
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth || 600;
    const cssH = canvas.clientHeight || 120;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, cssH);
    const padL = 34, padR = 8, padT = 8, padB = 16;
    const w = cssW - padL - padR;
    const h = cssH - padT - padB;
    const startTs = nowTs - WINDOW_MS;
    const style = getComputedStyle(document.body);
    const gridColor = style.getPropertyValue('--border').trim() || '#333';
    const mutedColor = style.getPropertyValue('--muted').trim() || '#888';
    // Horizontal gridlines + y labels (0, max/2, max).
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = mutedColor;
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    for (let i = 0; i <= 2; i++) {
        const val = (max / 2) * i;
        const y = padT + h - (val / max) * h;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(padL + w, y);
        ctx.globalAlpha = 0.5;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(Math.round(val)), padL - 5, y);
    }
    const xOf = (t) => padL + ((t - startTs) / WINDOW_MS) * w;
    const yOf = (v) => padT + h - (Math.max(0, Math.min(max, v)) / max) * h;
    if (points.length === 0) {
        ctx.fillStyle = mutedColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t('farmdev.waiting_for_data'), padL + w / 2, padT + h / 2);
        return;
    }
    // Area fill.
    ctx.beginPath();
    ctx.moveTo(xOf(points[0].t), padT + h);
    for (const p of points)
        ctx.lineTo(xOf(p.t), yOf(p.v));
    ctx.lineTo(xOf(points[points.length - 1].t), padT + h);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.12;
    ctx.fill();
    ctx.globalAlpha = 1;
    // Line.
    ctx.beginPath();
    points.forEach((p, i) => {
        const x = xOf(p.t), y = yOf(p.v);
        if (i === 0)
            ctx.moveTo(x, y);
        else
            ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.6;
    ctx.lineJoin = 'round';
    ctx.stroke();
}
export function renderHealthTab(panel, farmId, _farmName) {
    panel.innerHTML = `
        <p class="settings-hint">${t('farmdev.health_hint')}</p>
        <div class="health-stats" id="health-stats">
            <div class="health-stat" title="${t('farmdev.health_cpu_hint')}">
                <span class="health-stat__label">${t('farmdev.health_cpu')}</span><span class="health-stat__value" data-k="cpu">—</span></div>
            <div class="health-stat" title="${t('farmdev.health_mem_hint')}">
                <span class="health-stat__label">${t('farmdev.health_mem')}</span><span class="health-stat__value" data-k="mem">—</span></div>
            <div class="health-stat" title="${t('farmdev.health_load_hint')}">
                <span class="health-stat__label">${t('farmdev.health_load')}</span><span class="health-stat__value" data-k="load">—</span></div>
            <div class="health-stat" title="${t('farmdev.health_dev_hint')}">
                <span class="health-stat__label">${t('farmdev.health_dev')}</span><span class="health-stat__value" data-k="dev">—</span></div>
            <div class="health-stat" title="${t('farmdev.health_up_hint')}">
                <span class="health-stat__label">${t('farmdev.health_up')}</span><span class="health-stat__value" data-k="up">—</span></div>
        </div>
        <div class="health-chart">
            <div class="health-chart__title">${t('farmdev.health_cpu_chart')} <span class="health-chart__unit">%</span></div>
            <canvas class="health-canvas" id="health-cpu"></canvas>
        </div>
        <div class="health-chart">
            <div class="health-chart__title">${t('farmdev.health_mem_chart')} <span class="health-chart__unit">%</span></div>
            <canvas class="health-canvas" id="health-mem"></canvas>
        </div>
    `;
    const cpuCanvas = panel.querySelector('#health-cpu');
    const memCanvas = panel.querySelector('#health-mem');
    const statEl = (k) => panel.querySelector(`.health-stat__value[data-k="${k}"]`);
    const samples = [];
    const redraw = () => {
        const now = Date.now();
        while (samples.length && now - samples[0].ts > WINDOW_MS)
            samples.shift();
        const cpuPts = samples.map((s) => ({ t: s.ts, v: s.cpuPct }));
        const memPts = samples.map((s) => ({ t: s.ts, v: s.memPct }));
        drawChart(cpuCanvas, cpuPts, now, 100, '#ee384e');
        drawChart(memCanvas, memPts, now, 100, '#3b82f6');
        const last = samples[samples.length - 1];
        if (last) {
            statEl('cpu').textContent = t('farmdev.cpu_value', { pct: last.cpuPct, cores: last.cores });
            statEl('mem').textContent = t('farmdev.mem_value', {
                pct: last.memPct,
                used: (last.memUsedMb / 1024).toFixed(1),
                total: (last.memTotalMb / 1024).toFixed(1),
            });
            statEl('load').textContent = String(last.load1);
            statEl('dev').textContent = `${last.devicesOnline}/${last.devicesTotal}`;
            statEl('up').textContent = fmtUptime(last.uptimeSec);
        }
    };
    const onHealth = (ev) => {
        // Self-clean: once this tab's panel is gone from the DOM, drop the listener.
        if (!document.body.contains(panel)) {
            window.removeEventListener('farm-health', onHealth);
            window.removeEventListener('resize', onResize);
            return;
        }
        const detail = ev.detail;
        if (detail?.farmId !== farmId || !detail.health)
            return;
        samples.push(detail.health);
        redraw();
    };
    const onResize = () => redraw();
    window.addEventListener('farm-health', onHealth);
    window.addEventListener('resize', onResize);
    redraw();
}
